import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit, Renderer2 } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterLink, RouterOutlet } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { NgHttpLoaderComponent } from 'ng-http-loader';
import { combineLatest } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { loadStyleBundle } from 'src/app/core/utilities';
import { LocaleService } from './core/locale/locale.service';
import { HeaderComponent } from './header/header.component';
import { LoadCatalogError } from './root-store/catalog-store/actions';
import { selectCatalog } from './root-store/catalog-store/selectors';
import { CatalogSettings, CatalogState } from './root-store/catalog-store/state';
import { RootState } from './root-store/root-state.model';
import { selectIsAdminRoute, selectIsInfoScreenRoute, selectOrderId } from './root-store/router/selectors';
import { selectIsPrintView } from './root-store/scheduler-store/selectors';
import { selectUser } from './root-store/user-store/selectors';
import { FooterComponent } from './shared/footer/footer.component';
import { LoadSpinnerComponent } from './shared/load-spinner/load-spinner.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
  imports: [CommonModule, NgHttpLoaderComponent, HeaderComponent, RouterOutlet, RouterLink, FooterComponent, TranslateModule, MatButtonModule]
})
export class AppComponent implements OnInit {
  initialDataLoaded = false
  initialDataLoadError?: string
  loadSpinner = LoadSpinnerComponent
  cookiesSupported = true
  catalog?: CatalogState
  showBookingLayout?: boolean
  settings?: CatalogSettings
  showFooter = true

  constructor(
    private store: Store<RootState>,
    private actions$: Actions,
    private localeService: LocaleService,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer
  ) {
    // Add custom mat-icons, according to https://alligator.io/angular/custom-svg-icons-angular-material/
    ['pdf'].forEach(i => this.matIconRegistry.addSvgIcon(i, this.domSanitizer.bypassSecurityTrustResourceUrl(`assets/icons/${i}.svg`)))
  }

  async ngOnInit() {
    await this.localeService.initTranslationsAsync()

    // handle display in iFrame
    if (window.self !== window.top) {
      if (!this.localeService.getCultureFromCookie())
        this.cookiesSupported = false

      // if orderId is present in iframe (either editing an order or order-status): hide the footer
      this.store.select(selectOrderId).subscribe(orderId => {
        this.showFooter = !orderId

        // if window shows a different CatName: show the footer
        if (!this.showFooter) {
          try {
            const parentWindowCatName = window.top?.location.pathname.match('/([^/]+)');
            this.store.select(selectCatalog).pipe(filter(cat => !!cat.id), take(1)).subscribe(catalog => {
              if (!parentWindowCatName || catalog.name !== parentWindowCatName[1])
                this.showFooter = true
            })
          } catch {
            this.showFooter = true
          }
        }
      })
    }

    combineLatest([
      this.store.select(selectCatalog).pipe(filter(cat => !!cat.id)),
      this.store.select(selectUser).pipe(filter(user => user.isAuthenticated != null)),
    ]).pipe(
      take(1),
      tap(([catalog]) => {
        this.initialDataLoaded = true
        this.catalog = catalog
        this.settings = catalog.settings

        // preload additional components (with a delay, so PageSpeed is high for the initial page load)
        setTimeout(() => {
          loadStyleBundle('kendo')
          import('./scheduler/scheduler/scheduler.component')
        }, 1000)
      })).subscribe()

    this.actions$.pipe(ofType(LoadCatalogError), take(1)).subscribe(
      e => this.initialDataLoadError = e.error
    )

    // show booking layout on all routes except admin and info-screen
    combineLatest([
      this.store.select(selectIsAdminRoute),
      this.store.select(selectIsInfoScreenRoute),
    ])
      .subscribe(([adminRoute, infoScreenRoute]) =>
        this.showBookingLayout = !adminRoute && infoScreenRoute === false
      )

    this.store.select(selectIsPrintView).subscribe(printView => {
      if (printView)
        this.renderer.addClass(this.document.body, 'print-view')
      else
        this.renderer.removeClass(this.document.body, 'print-view')
    })
  }
}
